import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../../apiClient';

type BucketSyncStatus = 'unsynced-with-code' | 'unsynced-with-cloud' | 'synced';

type BucketAccessStatus = 'active' | 'inactive' | 'error' | 'no-creds';

export type S3Bucket = {
  name: string;
  region?: string;
  id: string;
  syncStatus: BucketSyncStatus;
  accessStatus: BucketAccessStatus;
  errors?: {
    id: string;
    type: 'role' | 'credential';
    name: string;
  }[];
};

export type HCPCloudTokenStatus = 'active' | 'inactive' | 'error';

export type HCPCloudToken = {
  url: string;
  workspace: string;
  id: string;
  token: string;
  status: HCPCloudTokenStatus;
  name: string;
};

export type HCPCloudTokenForCreate = Omit<HCPCloudToken, 'id' | 'status'>;
export type HCPCloudTokenForList = Exclude<HCPCloudToken, 'token'>;

export const terraformBackendHcpCloudQueries = {
  all: () => ['terraform-backend', 'hcp-cloud-tokens'],
  byProject: (projectId: string) => [
    ...terraformBackendHcpCloudQueries.all(),
    projectId,
  ],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [
        ...terraformBackendHcpCloudQueries.byProject(projectId),
        'list',
      ],
      queryFn: async () => {
        const hcpCloudTokens = await listHcpCloudTokens(projectId);

        return hcpCloudTokens;
      },
    }),
  details: (projectId: string) => [
    ...terraformBackendHcpCloudQueries.byProject(projectId),
    'details',
  ],
  detail: (projectId: string, tokenId: string) =>
    queryOptions({
      queryKey: [
        ...terraformBackendHcpCloudQueries.details(projectId),
        tokenId,
      ],
      queryFn: async () => {
        const hcpCloudToken = await getHcpCloudToken(projectId, tokenId);

        return hcpCloudToken;
      },
    }),
} as const;

const listHcpCloudTokens = async (
  projectId: string,
): Promise<HCPCloudTokenForList[]> => {
  const getResponse = await fetchBackend(
    `/terraform-backend/hcp-cloud/${projectId}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    hcpCloudTokens: HCPCloudTokenForList[];
  };

  return data.hcpCloudTokens;
};

const getHcpCloudToken = async (
  projectId: string,
  tokenId: string,
): Promise<HCPCloudToken> => {
  const getResponse = await fetchBackend(
    `/terraform-backend/hcp-cloud/${projectId}/${tokenId}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    hcpCloudToken: HCPCloudToken;
  };

  return data.hcpCloudToken;
};

export const deleteHcpCloudToken = async (
  projectId: string,
  tokenId: string,
): Promise<void> => {
  const deleteResponse = await fetchBackend(
    `/terraform-backend/hcp-cloud/${projectId}/${tokenId}`,
    {
      method: 'DELETE',
    },
  );

  if (!deleteResponse.ok) {
    throw new Error(`Error: ${deleteResponse.statusText}`);
  }
};

export const upsertHCPCloudToken = async (
  projectId: string,
  payload: HCPCloudTokenForCreate,
  tokenId?: string,
): Promise<HCPCloudTokenForCreate> => {
  if (tokenId !== undefined) {
    const updateResponse = await fetchBackend(
      `/terraform-backend/hcp-cloud/${projectId}/${tokenId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );

    if (!updateResponse.ok) {
      throw new Error(`Error: ${updateResponse.statusText}`);
    }

    return payload;
  }

  const createResponse = await fetchBackend(
    `/terraform-backend/hcp-cloud/${projectId}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );

  if (!createResponse.ok) {
    throw new Error(`Error: ${createResponse.statusText}`);
  }

  return payload;
};
